import { ActionIcon, Badge, Button, Checkbox, Group, Text } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	describeAlreadySent,
	describeIndividualSchedule,
	describeSignature,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentPickerColumns.utils';
import { DocumentWorkflowTemplateBadges } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/DocumentWorkflowTemplateTable/DocumentWorkflowTemplateBadges';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { ChevronSlot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentPickerColumns.styled';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import type { BuildDocumentPickerColumnsParams } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentPickerColumns.types';

const SELECT_COLUMN_WIDTH = 48;
const EXPAND_COLUMN_WIDTH = 116;
const PREVIEW_COLUMN_WIDTH = 56;

/** Columns for the send-documents picker table: selection, name, classifiers and document settings. */
export const buildDocumentPickerColumns = ({
	tMipManage,
	tCommonUi,
	onPreview,
}: BuildDocumentPickerColumnsParams): ExtendedColumnDef<DocumentWorkflowTemplateInfo>[] => [
	{
		id: 'select',
		header: ({ table }) => (
			<Checkbox
				data-testid={TEST_ELEMENT_IDS.CAP_TABLE_SELECT_ALL_ON_PAGE_CHECKBOX}
				checked={table.getIsAllRowsSelected()}
				indeterminate={table.getIsSomeRowsSelected()}
				onChange={table.getToggleAllRowsSelectedHandler()}
				onClick={(event) => event.stopPropagation()}
			/>
		),
		enableSorting: false,
		enableColumnFilter: false,
		size: SELECT_COLUMN_WIDTH,
		minSize: SELECT_COLUMN_WIDTH,
		maxSize: SELECT_COLUMN_WIDTH,
		cell: ({ row }) => (
			<Checkbox
				data-testid={`send-documents-row-checkbox-${row.original.id}`}
				checked={row.getIsSelected()}
				disabled={!row.getCanSelect()}
				onChange={row.getToggleSelectedHandler()}
				onClick={(event) => event.stopPropagation()}
			/>
		),
	},
	createTextColumn({
		headerText: tMipManage('master_documents.header.name'),
		columnLabel: tMipManage('master_documents.header.name'),
		filterText: tMipManage('master_documents.header.name'),
		showFilter: true,
		accessorKey: 'name',
	}),
	createTextColumn({
		id: 'type',
		headerText: tMipManage('master_documents.header.type'),
		columnLabel: tMipManage('master_documents.header.type'),
		filterText: tMipManage('master_documents.header.type'),
		showFilter: true,
		enableSorting: false,
		// The cell renders badges, so this value exists only for search, filtering and sorting:
		// the names themselves, not the count they used to report.
		accessorFn: (row) => (row.classifiers ?? []).join(', '),
		cell: ({ row }) => {
			const classifiers = row.original.classifiers ?? [];

			if (classifiers.length === 0) {
				return <Text size="B6Medium">{LONG_DASH}</Text>;
			}

			const [firstClassifier] = classifiers;

			return (
				<Group gap="xs" wrap="nowrap">
					<DocumentWorkflowTemplateBadges
						items={[firstClassifier]}
						formatItem={(name: string) => name}
					/>
					{classifiers.length > 1 && (
						<Badge
							size="xs"
							variant="filled"
							color="gray"
							radius="xl"
							sx={{
								backgroundColor: defaultTheme.v3.colors.gray[4],
								color: defaultTheme.v3.colors.dark[4],
								fontWeight: 'normal',
								fontSize: '12px',
							}}
						>
							+{classifiers.length - 1}
						</Badge>
					)}
				</Group>
			);
		},
	}),
	createTextColumn({
		id: 'signature',
		headerText: tMipManage('master_documents.header.signature'),
		columnLabel: tMipManage('master_documents.header.signature'),
		filterText: tMipManage('master_documents.header.signature'),
		showFilter: true,
		enableSorting: false,
		accessorFn: (row) =>
			describeSignature(tMipManage, { type: row.type, signatureType: row.signatureType }),
	}),
	createTextColumn({
		id: 'individualSchedule',
		headerText: tMipManage('send_documents.row_editor.individual_schedule'),
		columnLabel: tMipManage('send_documents.row_editor.individual_schedule'),
		filterText: tMipManage('send_documents.row_editor.individual_schedule'),
		showFilter: true,
		enableSorting: false,
		accessorFn: (row) =>
			describeIndividualSchedule(tCommonUi, {
				individualScheduleRequired: row.individualScheduleRequired,
			}),
	}),
	createTextColumn({
		id: 'alreadySent',
		headerText: tMipManage('send_documents.column.already_sent'),
		columnLabel: tMipManage('send_documents.column.already_sent'),
		filterText: tMipManage('send_documents.column.already_sent'),
		showFilter: true,
		enableSorting: false,
		accessorFn: (row) =>
			describeAlreadySent(tCommonUi, { issuedDocumentCount: row.issuedDocumentCount }),
	}),
	{
		id: 'preview',
		header: '',
		// Chrome, not data. Without this it lists in the Columns menu as a nameless switch
		// (the empty header wins the label lookup) that hides the preview control for good.
		meta: { disableToggleVisibility: true },
		enableSorting: false,
		enableColumnFilter: false,
		size: PREVIEW_COLUMN_WIDTH,
		minSize: PREVIEW_COLUMN_WIDTH,
		maxSize: PREVIEW_COLUMN_WIDTH,
		cell: ({ row }) => (
			<Group justify="center" w="100%" gap={0}>
				<ActionIcon
					data-testid={`send-documents-row-preview-${row.original.id}`}
					variant="subtle"
					aria-label={tMipManage('send_documents.picker.preview')}
					// Nothing to open for a document with no stored file yet.
					disabled={!row.original.file}
					onClick={(event) => {
						event.stopPropagation();
						onPreview(row.original.id);
					}}
				>
					<Icon variant="eye" size="lg" color={defaultTheme.v3.colors.dark[4]} />
				</ActionIcon>
			</Group>
		),
	},
	{
		id: 'expand',
		header: '',
		meta: { disableToggleVisibility: true },
		enableSorting: false,
		enableColumnFilter: false,
		size: EXPAND_COLUMN_WIDTH,
		minSize: EXPAND_COLUMN_WIDTH,
		maxSize: EXPAND_COLUMN_WIDTH,
		cell: ({ row }) => (
			// The column is wider than the control, so centre it rather than let it hug the
			// left edge with the slack all on one side.
			<Group justify="center" w="100%" gap={0}>
				<Button
					data-testid={`send-documents-row-edit-${row.original.id}`}
					variant="subtle"
					// Figma: Sans/B6*H6 (14)/Semi Bold. Mantine's `xs` label is 12px, so the size
					// and weight are pinned rather than left to the scale.
					size="sm"
					fz="sm"
					fw={600}
					styles={{ inner: { gap: '0.5rem' } }}
					onClick={(event) => {
						event.stopPropagation();
						row.toggleExpanded();
					}}
					// The chevron states which way the row will move, so it doubles as the expanded
					// indicator the Edit label alone cannot give. Figma draws it at 24px — Icon `xl`,
					// since `md` on this scale is 12px — and in black, not the label's blue.
					// Rotated rather than swapped for `chevron-top`: that asset is a filled
					// triangle with a hardcoded #151819 fill, so the two states would differ in
					// weight and ignore the colour set here.
					rightSection={
						<ChevronSlot>
							<Icon
								variant="chevron-down"
								size="xl"
								color={defaultTheme.v3.colors.black[0]}
								style={{
									transform: row.getIsExpanded() ? 'rotate(180deg)' : undefined,
								}}
							/>
						</ChevronSlot>
					}
				>
					{row.getIsExpanded()
						? tMipManage('send_documents.row_editor.hide')
						: tCommonUi('common_buttons.edit')}
				</Button>
			</Group>
		),
	},
];
